import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { IntlProvider } from 'react-intl'
import { 
  SupportedLocale, 
  defaultLocale, 
  messages, 
  getLocaleFromPath, 
  getPathWithoutLocale, 
  getLocalizedPath 
} from '../i18n'

interface LanguageContextType {
  locale: SupportedLocale
  changeLanguage: (newLocale: SupportedLocale) => void
  getLocalizedPath: (path: string) => string
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}

interface LanguageProviderProps {
  children: ReactNode
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const location = useLocation()
  const navigate = useNavigate()
  
  const [locale, setLocale] = useState<SupportedLocale>(() => {
    return getLocaleFromPath(location.pathname)
  })

  useEffect(() => {
    const newLocale = getLocaleFromPath(location.pathname)
    if (newLocale !== locale) {
      setLocale(newLocale)
    }
  }, [location.pathname, locale])

  const changeLanguage = (newLocale: SupportedLocale) => {
    const currentPath = getPathWithoutLocale(location.pathname)
    const newPath = getLocalizedPath(currentPath, newLocale)
    navigate(newPath)
  }

  const getLocalizedPathForCurrentLocale = (path: string): string => {
    return getLocalizedPath(path, locale)
  }

  const contextValue: LanguageContextType = {
    locale,
    changeLanguage,
    getLocalizedPath: getLocalizedPathForCurrentLocale,
  }

  // 确保messages[locale]存在，否则使用默认语言
  const currentMessages = messages[locale] || messages[defaultLocale]

  return (
    <LanguageContext.Provider value={contextValue}>
      <IntlProvider
        locale={locale}
        messages={currentMessages}
        defaultLocale={defaultLocale}
        onError={(error) => {
          console.warn('React Intl Error:', error)
        }}
      >
        {children}
      </IntlProvider>
    </LanguageContext.Provider>
  )
}